/**
 * An extras rule that changes nothing today says so on its card (user,
 * 12/10/2026: "no secret"). Exact, not guessed: the day's extra drops are
 * placed with every rule, then again without this one — the same drops at
 * the same minutes means the rule changes nothing today, and the note says
 * why in one line.
 * For Media Devoted | Created By Ciro Autiero
 */
import type { DropGenHourBucket, DropGenRule } from '@trafficpulse/shared';

import { addedMinutes, type ExtrasSettings, type ExtrasSpan, previewExtras, roomByHour } from './extras-preview';

/** The rules that only move extra drops — a block also removes the hour's own drops, so it always matters. */
const EXTRAS_ONLY = new Set(['extra_first', 'no_extra']);

const without = (buckets: ReadonlyArray<DropGenHourBucket>, ruleId: string): DropGenHourBucket[] =>
  buckets.map((b) => ({ ...b, ruleHits: (b.ruleHits ?? []).filter((h) => h.ruleId !== ruleId) }));

function why(rule: DropGenRule, firesInSpan: boolean, anyExtra: boolean, s: ExtrasSettings): string {
  if (!firesInSpan) return 'It fires on none of this container’s hours today — it changes nothing.';
  if (s.maxExtraSlots === 0 && rule.action === 'extra_first') return 'Extra drops is 0 (Config) — it changes nothing.';
  if (!anyExtra) return 'No extra drop fits today (see the live panel) — it changes nothing yet.';
  return rule.action === 'extra_first'
    ? 'Its hours are the ones the ranking already tries first — it changes nothing today.'
    : 'Its hours would get no extra drop anyway — it changes nothing today.';
}

/** ruleId → the note, for every enabled extras rule that changes nothing today. */
export function ruleNotes(buckets: ReadonlyArray<DropGenHourBucket>, span: ExtrasSpan, s: ExtrasSettings, rules: ReadonlyArray<DropGenRule>): Record<string, string> {
  const base = previewExtras(buckets, span, s);
  const hours = roomByHour(span);
  const out: Record<string, string> = {};
  for (const rule of rules) {
    if (!rule.enabled || !EXTRAS_ONLY.has(rule.action)) continue;
    if (addedMinutes(previewExtras(without(buckets, rule.id), span, s)) !== addedMinutes(base)) continue;
    const firesInSpan = buckets.some((b) => hours.has(b.hour) && (b.ruleHits ?? []).some((h) => h.ruleId === rule.id && h.state === 'fired'));
    out[rule.id] = why(rule, firesInSpan, base.extras.length > 0 || base.probe !== null, s);
  }
  return out;
}
